/**
 * Corregir voz — la parte pura de la sheet: normalizar el transcript de Scribe, agrupar
 * palabras en oraciones para mostrarlas (misma regla que `services/corregirVoz/plan.ts`
 * del backend: puntuación final o pausa > 0.6 s) y armar las ediciones que viajan al
 * backend (sólo las palabras que cambiaron de verdad).
 * ADR-2026-09-21-corregir-voz-es-una-creacion-nueva-gratis.
 */
export interface PalabraLab {
  text: string;
  start: number;
  end: number;
  type?: string;
}

export interface EdicionLab {
  indice: number;
  texto: string;
}

export const PAUSA_QUE_CORTA_ORACION = 0.6;
const TERMINA_ORACION = /[.!?…]["»)\]]*$/;

export function esHablada(p: PalabraLab | undefined): p is PalabraLab {
  return !!p && (p.type === undefined || p.type === 'word') && p.text.trim().length > 0;
}

/** Scribe crudo, `[raw]`, `{data: raw}` → palabras; null si no tiene el shape. */
export function normalizarTranscript(raw: unknown): PalabraLab[] | null {
  const t = Array.isArray(raw)
    ? raw[0]
    : raw && typeof raw === 'object' && 'data' in raw
      ? (raw as { data: unknown }).data
      : raw;
  const words = (t as { words?: unknown } | null)?.words;
  if (!Array.isArray(words)) return null;
  const out: PalabraLab[] = [];
  for (const w of words) {
    if (!w || typeof w !== 'object') continue;
    const { text, start, end, type } = w as Record<string, unknown>;
    if (typeof text !== 'string' || typeof start !== 'number' || typeof end !== 'number') continue;
    out.push({ text, start, end, ...(typeof type === 'string' ? { type } : {}) });
  }
  return out;
}

/** Grupos de índices (del array original) de palabras habladas, una lista por oración. */
export function agruparEnOraciones(palabras: PalabraLab[], pausa = PAUSA_QUE_CORTA_ORACION): number[][] {
  const hablados: number[] = [];
  palabras.forEach((p, i) => {
    if (esHablada(p)) hablados.push(i);
  });
  const oraciones: number[][] = [];
  let actual: number[] = [];
  hablados.forEach((idx, k) => {
    actual.push(idx);
    const p = palabras[idx];
    const siguiente = hablados[k + 1];
    const hayPausa = siguiente !== undefined && palabras[siguiente].start - p.end > pausa;
    if (TERMINA_ORACION.test(p.text.trim()) || hayPausa || siguiente === undefined) {
      oraciones.push(actual);
      actual = [];
    }
  });
  return oraciones;
}

/** Sólo las palabras cuyo texto cambió (y no quedó vacío). */
export function edicionesDesde(palabras: PalabraLab[], textos: Record<number, string>): EdicionLab[] {
  const out: EdicionLab[] = [];
  for (const [k, v] of Object.entries(textos)) {
    const indice = Number(k);
    const p = palabras[indice];
    const texto = (v ?? '').trim();
    if (!esHablada(p) || !texto || texto === p.text.trim()) continue;
    out.push({ indice, texto });
  }
  return out.sort((a, b) => a.indice - b.indice);
}

/**
 * Las oraciones que se escuchan en la timeline: en un clip recortado, lo que queda fuera de
 * `[desde, hasta)` (segundos del archivo) no suena, y corregirlo no cambia nada de lo que
 * el usuario ve. Una oración que cruza el borde entra entera: se regenera completa.
 */
export function oracionesEnLaVentana(
  palabras: PalabraLab[],
  ventana: { desde: number; hasta: number },
): number[][] {
  return agruparEnOraciones(palabras).filter((indices) =>
    indices.some((i) => palabras[i].end > ventana.desde && palabras[i].start < ventana.hasta),
  );
}
